import * as React from 'react';
import { useState, useEffect } from 'react';

import { ButtonTheme, Color } from '../../enums';
import { UseState } from '../../types';
import { Icon } from '../icon/icon.component';

import { BaseButton, IconContainer, LabelContainer, LoadingContainer, LoadingDot } from './button.styled';
import { ButtonProps } from './button.types';

export const Button: React.FC<ButtonProps> = (props: ButtonProps) => {
  const {
    label,
    onClick,
    buttonTheme = ButtonTheme.PrimaryBig,
    className,
    fullWidth = false,
    disabled = false,
    type = 'button',
    icon,
    isLoading,
    id
  }: ButtonProps = props;
  const [iconColor, setIconColor]: UseState<Color> = useState<Color>(Color.White);
  const isSecondarySolid: boolean = (buttonTheme === ButtonTheme.SecondarySolidSmall) || (buttonTheme === ButtonTheme.SecondarySolidBig);
  const isSecondaryTransparent: boolean = (buttonTheme === ButtonTheme.SecondaryTransparentSmall) ||
    (buttonTheme === ButtonTheme.SecondaryTransparentBig);
  const isDisabledOrGray: boolean = (disabled) || (buttonTheme === ButtonTheme.GrayTransparent);
  const updateIconColor: () => void = (): void => setIconColor(isDisabledOrGray
    ? Color.Gray400
    : isSecondarySolid
      ? Color.Black
      : Color.White);

  const onPointerDown: () => void = (): void => {
    if (!disabled) {
      if (isSecondaryTransparent) {
        setIconColor(Color.Gray400);
      } else if (isSecondarySolid) {
        setIconColor(Color.Black);
      }
    }
  };

  const onPointerEnter: () => void = (): void => {
    if (isSecondarySolid && !disabled) {
      setIconColor(Color.White);
    }
  };

  const onPointerUp: () => void = (): void => {
    if ((isSecondaryTransparent || isSecondarySolid) && !disabled) {
      setIconColor(Color.White);
    }
  };

  useEffect(
    (): void => {
      if (disabled) {
        setIconColor(Color.Gray400);
      } else {
        updateIconColor();
      }
    },
    [disabled, buttonTheme]
  );

  return (
    <BaseButton
      $theme={buttonTheme}
      $fullWidth={fullWidth}
      className={className}
      onClick={!isLoading ? onClick : undefined}
      disabled={disabled}
      type={type}
      onPointerEnter={onPointerEnter}
      onPointerLeave={updateIconColor}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      id={id}
    >
      {isLoading && (
        <LoadingContainer>
          <LoadingDot $color={iconColor} data-testid='first-dot' />
          <LoadingDot $color={iconColor} data-testid='second-dot' />
          <LoadingDot $color={iconColor} data-testid='third-dot' />
        </LoadingContainer>
      )}
      {icon && (
        <IconContainer $visible={!isLoading}>
          <Icon name={icon} size={24} color={iconColor} />
        </IconContainer>
      )}
      <LabelContainer $visible={!isLoading}>{label}</LabelContainer>
    </BaseButton>
  );
};
